import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiService } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  GitMerge,
  Edit,
  Trash2,
  Save,
  X,
  Loader2,
  Link as LinkIcon,
  Sparkles,
  Search,
  Layers,
  ArrowUpDown,
  Eye,
  Type,
  Database,
  FileCode,
  CheckCircle2,
  Circle,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Fingerprint,
  Lock
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

// ---- Pure helpers: a JS port of the backend render pipeline so the builder
// can show an accurate live preview of the XML that will be posted to Tally. ----

// Named repeat block {{#NAME}}...{{/NAME}} (optionally filtered: {{#NAME col=value}}).
// Mirrors the backend so the preview reflects multiple repeating sections (e.g. an
// items block + a tax-ledger block that varies per voucher). \1 ties the close tag.
const BLOCK_RE = /\{\{#(\w+)(?:\s+([^}]*?))?\}\}([\s\S]*?)\{\{\/\1\}\}/;
const BLOCK_FILTER_RE = /^\s*([\w.]+)\s*(!=|=)\s*(.*?)\s*$/;
const rowMatchesBlock = (row, filterExpr) => {
  if (!filterExpr || !filterExpr.trim()) return true;
  const m = filterExpr.match(BLOCK_FILTER_RE);
  if (!m) return true;
  const [, col, op, val] = m;
  const actual = row?.[col] == null ? '' : String(row[col]).trim();
  return op === '!=' ? actual !== val.trim() : actual === val.trim();
};

// Illustrative base used only to show realistic GUIDs in the live preview.
// The real base + sequence are generated server-side at post time.
const SAMPLE_GUID_BASE = '37f044b5-8cd6-48de-9011-8e4f7e01a659';
const sampleGuid = (index) => `${SAMPLE_GUID_BASE}-${String(index + 1).padStart(8, '0')}`;

const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const isGuidTag = (tag) => normalize(tag) === 'guid';

// Just the {{PLACEHOLDER}} names (used for GUID auto-detect). Block markers skipped.
const extractPlaceholders = (xml) => {
  const seen = new Set(), out = [];
  const re = /\{\{\s*([^}#/][^}]*?)\s*\}\}/g;
  let m;
  while ((m = re.exec(xml || '')) !== null) {
    const t = m[1].trim();
    if (t && !seen.has(t)) { seen.add(t); out.push(t); }
  }
  return out;
};

// Every mappable tag in the template: its {{placeholders}} (differentiated names
// like ITEM_AMOUNT / OUTPUT CGST@9%) followed by every raw XML element name, so
// ALL tags appear on the left. De-duplicated, order-stable.
const extractTags = (xml) => {
  const seen = new Set(), out = [];
  extractPlaceholders(xml).forEach((t) => { if (!seen.has(t)) { seen.add(t); out.push(t); } });
  const re = /<([A-Za-z][A-Za-z0-9_.]*)/g;
  let m;
  while ((m = re.exec(xml || '')) !== null) {
    const t = m[1];
    if (t && !seen.has(t)) { seen.add(t); out.push(t); }
  }
  return out;
};

// Escape a string for use inside a RegExp.
const reEsc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const fieldValue = (fm, row, guid) => {
  if (fm.type === 'guid') return guid || '';
  if (fm.type === 'column') {
    const v = row?.[fm.dataset_column];
    return v === undefined || v === null ? '' : String(v);
  }
  return fm.static_value || '';
};

const applyMappings = (xml, fms, row, scope, guid) => {
  let out = xml;
  fms.forEach((fm) => {
    if (scope && (fm.scope || 'header') !== scope) return;
    if (!fm.xml_tag) return;
    const val = fieldValue(fm, row, guid);
    const ph = `{{${fm.xml_tag}}}`;
    if (out.includes(ph)) {
      out = out.split(ph).join(val);
    } else {
      // No placeholder: map by element name — set the text of <TAG>…</TAG>
      // (leaf elements only; containers have child tags so they won't match).
      const esc = reEsc(fm.xml_tag);
      out = out.replace(new RegExp('(<' + esc + '(?:\\s[^>]*)?>)([^<]*)(</' + esc + '>)', 'g'), (mm, a, b, c) => a + val + c);
    }
  });
  return out;
};

// Mirror of the backend: stamp the per-voucher GUID into the <VOUCHER> REMOTEID
// so the preview matches what gets posted (REMOTEID is filled even when the
// template leaves it blank). \b after VOUCHER avoids matching <VOUCHERTYPENAME>.
const VOUCHER_REMOTEID_RE = /(<VOUCHER\b[^>]*?\bREMOTEID=")[^"]*(")/i;
const VOUCHER_OPEN_RE = /<VOUCHER\b/i;
const setVoucherRemoteId = (xml, guid) => {
  if (!guid) return xml;
  if (VOUCHER_REMOTEID_RE.test(xml)) return xml.replace(VOUCHER_REMOTEID_RE, (_, a, b) => a + guid + b);
  if (VOUCHER_OPEN_RE.test(xml)) return xml.replace(VOUCHER_OPEN_RE, `<VOUCHER REMOTEID="${guid}"`);
  return xml;
};
// Blank any placeholder no mapping filled, matching the backend.
const stripUnfilled = (xml) => xml.replace(/\{\{[^}]+\}\}/g, '');

const buildVoucherXml = (tmpl, fms, group, guid) => {
  const hasGuid = fms.some((fm) => fm.type === 'guid');
  const header = group[0] || {};
  // Expand every {{#NAME [filter]}}...{{/NAME}} block: each repeats once per
  // matching row (line scope). Fresh global regex to avoid shared lastIndex.
  const re = new RegExp(BLOCK_RE.source, 'g');
  let out = '', pos = 0, m, found = false;
  while ((m = re.exec(tmpl)) !== null) {
    found = true;
    out += tmpl.slice(pos, m.index);
    const filterExpr = m[2], body = m[3];
    const rows = group.filter((r) => rowMatchesBlock(r, filterExpr));
    out += rows.map((r) => applyMappings(body, fms, r, 'line', guid)).join('');
    pos = m.index + m[0].length;
  }
  let xml;
  if (found) {
    out += tmpl.slice(pos);
    xml = applyMappings(out, fms, header, 'header', guid);
  } else {
    xml = applyMappings(tmpl, fms, header, null, guid);
  }
  // Auto-stamp REMOTEID with the GUID (only when a GUID field exists), then drop
  // any leftover {{TAG}} — same as the server render.
  if (hasGuid) xml = setVoucherRemoteId(xml, guid);
  return stripUnfilled(xml);
};

const sortRows = (rows, orderBy) => {
  if (!orderBy?.length) return rows;
  const arr = [...rows];
  [...orderBy].reverse().forEach((spec) => {
    const col = spec.column;
    const desc = spec.direction === 'desc';
    arr.sort((a, b) => {
      const av = a?.[col];
      const bv = b?.[col];
      let r;
      if (av == null && bv == null) r = 0;
      else if (av == null) r = -1;
      else if (bv == null) r = 1;
      else if (typeof av === 'number' && typeof bv === 'number') r = av - bv;
      else r = String(av).localeCompare(String(bv));
      return desc ? -r : r;
    });
  });
  return arr;
};

const groupRows = (rows, groupBy) => {
  if (!groupBy?.length) return rows.map((r) => [r]);
  const map = new Map();
  rows.forEach((row) => {
    const key = groupBy.map((c) => String(row?.[c] ?? '')).join('');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  return [...map.values()];
};

const MappingsPage = () => {
  const [mappings, setMappings] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingMapping, setDeletingMapping] = useState(null);

  // Form state
  const [mappingForm, setMappingForm] = useState({
    name: '',
    dataset_id: '',
    xml_template_id: '',
    field_mappings: [],
    group_by: [],
    order_by: []
  });

  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Builder UX state
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | mapped | unmapped
  const [sideTab, setSideTab] = useState('preview');
  const [sampleRows, setSampleRows] = useState([]);
  const [sampleLoading, setSampleLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mappingsRes, datasetsRes, templatesRes] = await Promise.all([
        apiService.getMappings(),
        apiService.getDatasets(),
        apiService.getXMLTemplates()
      ]);
      setMappings(mappingsRes.data);
      setDatasets(datasetsRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load sample rows for the live preview whenever the dataset changes.
  const loadSampleRows = useCallback(async (datasetId) => {
    if (!datasetId) {
      setSampleRows([]);
      return;
    }
    setSampleLoading(true);
    try {
      const res = await apiService.previewDataset(datasetId, 50);
      setSampleRows(res.data.rows || []);
      // Keep the in-memory dataset columns in sync (a never-previewed dataset
      // may have an empty columns list until now).
      const cols = res.data.columns;
      if (cols?.length) {
        setSelectedDataset((prev) => (prev && prev.id === datasetId ? { ...prev, columns: cols } : prev));
      }
    } catch (error) {
      setSampleRows([]);
    } finally {
      setSampleLoading(false);
    }
  }, []);

  const openBuilder = (mapping = null) => {
    if (mapping) {
      setSelectedMapping(mapping);
      const ds = datasets.find((d) => d.id === mapping.dataset_id);
      const tmpl = templates.find((t) => t.id === mapping.xml_template_id);
      setSelectedDataset(ds);
      setSelectedTemplate(tmpl);
      setMappingForm({
        name: mapping.name,
        dataset_id: mapping.dataset_id,
        xml_template_id: mapping.xml_template_id,
        field_mappings: mapping.field_mappings || [],
        group_by: mapping.group_by || [],
        order_by: mapping.order_by || []
      });
      loadSampleRows(mapping.dataset_id);
    } else {
      setSelectedMapping(null);
      setSelectedDataset(null);
      setSelectedTemplate(null);
      setSampleRows([]);
      setMappingForm({
        name: '',
        dataset_id: '',
        xml_template_id: '',
        field_mappings: [],
        group_by: [],
        order_by: []
      });
    }
    setSearch('');
    setFilterMode('all');
    setSideTab('preview');
    setIsBuilderOpen(true);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setSelectedMapping(null);
    setSelectedDataset(null);
    setSelectedTemplate(null);
    setSampleRows([]);
  };

  const handleDatasetChange = (datasetId) => {
    const ds = datasets.find((d) => d.id === datasetId);
    setSelectedDataset(ds);
    setMappingForm((prev) => ({ ...prev, dataset_id: datasetId, field_mappings: [], group_by: [], order_by: [] }));
    loadSampleRows(datasetId);
  };

  const handleTemplateChange = (templateId) => {
    const tmpl = templates.find((t) => t.id === templateId);
    setSelectedTemplate(tmpl);
    // Auto-tag any GUID tag with the auto-generated guid type.
    const autoFields = extractTags(tmpl?.xml_content)
      .filter(isGuidTag)
      .map((tag) => ({ xml_tag: tag, type: 'guid', scope: 'header' }));
    setMappingForm((prev) => ({ ...prev, xml_template_id: templateId, field_mappings: autoFields }));
  };

  // ---- Field mapping mutations (keyed by xml_tag) ----

  // O(1) tag -> field lookup, rebuilt only when mappings change.
  const fieldByTag = useMemo(() => {
    const m = new Map();
    mappingForm.field_mappings.forEach((f) => m.set(f.xml_tag, f));
    return m;
  }, [mappingForm.field_mappings]);

  const getField = useCallback((tag) => fieldByTag.get(tag), [fieldByTag]);

  // Stable callbacks (functional updates only) so memoized TagRows don't
  // re-render when an unrelated field changes.
  const upsertField = useCallback((tag, patch) => {
    setMappingForm((prev) => {
      const idx = prev.field_mappings.findIndex((m) => m.xml_tag === tag);
      let fms;
      if (idx >= 0) {
        fms = [...prev.field_mappings];
        fms[idx] = { ...fms[idx], ...patch };
      } else {
        fms = [...prev.field_mappings, { xml_tag: tag, type: 'column', scope: 'header', ...patch }];
      }
      return { ...prev, field_mappings: fms };
    });
  }, []);

  const clearField = useCallback((tag) => {
    setMappingForm((prev) => ({
      ...prev,
      field_mappings: prev.field_mappings.filter((m) => m.xml_tag !== tag)
    }));
  }, []);

  const setFieldColumn = useCallback(
    (tag, column) => upsertField(tag, { type: 'column', dataset_column: column, static_value: undefined }),
    [upsertField]
  );
  const setFieldStatic = useCallback(
    (tag, value) => upsertField(tag, { type: 'static', static_value: value, dataset_column: undefined }),
    [upsertField]
  );
  const setFieldSourceType = useCallback((tag, type) => {
    setMappingForm((prev) => {
      const idx = prev.field_mappings.findIndex((m) => m.xml_tag === tag);
      const existing = idx >= 0 ? prev.field_mappings[idx] : null;
      if (existing && existing.type === type) return prev;
      // Switching to GUID clears any column/static value.
      const patch = type === 'guid' ? { type, dataset_column: undefined, static_value: undefined } : { type };
      let fms;
      if (idx >= 0) {
        fms = [...prev.field_mappings];
        fms[idx] = { ...fms[idx], ...patch };
      } else {
        fms = [...prev.field_mappings, { xml_tag: tag, type: 'column', scope: 'header', ...patch }];
      }
      return { ...prev, field_mappings: fms };
    });
  }, []);
  const setFieldScope = useCallback((tag, scope) => upsertField(tag, { scope }), [upsertField]);
  // Pin/freeze: save this field's value on the first post and reuse it on every
  // repost, so its value in Tally never changes (e.g. keeps a renamed ledger's
  // original name so Tally updates it in place instead of creating a new one).
  const setFieldPin = useCallback((tag, pin) => upsertField(tag, { pin }), [upsertField]);

  // Smart auto-map: match XML tags to dataset columns by normalized name.
  const autoMap = () => {
    if (!selectedTemplate || !selectedDataset) return;
    const cols = selectedDataset.columns || [];
    // Columns are table-qualified ("table.column"); match a tag against the
    // column's last segment so auto-map still works.
    const colByNorm = new Map(cols.map((c) => [normalize(c.split('.').pop()), c]));
    let count = 0;
    setMappingForm((prev) => {
      const fms = [...prev.field_mappings];
      extractTags(selectedTemplate.xml_content).forEach((tag) => {
        if (fms.some((m) => m.xml_tag === tag)) return;
        if (isGuidTag(tag)) {
          fms.push({ xml_tag: tag, type: 'guid', scope: 'header' });
          count += 1;
          return;
        }
        const match = colByNorm.get(normalize(tag));
        if (match) {
          fms.push({ xml_tag: tag, type: 'column', dataset_column: match, scope: 'header' });
          count += 1;
        }
      });
      return { ...prev, field_mappings: fms };
    });
    toast.success(count ? `Auto-mapped ${count} field${count > 1 ? 's' : ''}` : 'No name matches found');
  };

  // ---- Grouping mutations ----

  const toggleGroupBy = (column) => {
    setMappingForm((prev) => ({
      ...prev,
      group_by: prev.group_by.includes(column)
        ? prev.group_by.filter((c) => c !== column)
        : [...prev.group_by, column]
    }));
  };

  const toggleOrderBy = (column) => {
    setMappingForm((prev) => ({
      ...prev,
      order_by: prev.order_by.some((o) => o.column === column)
        ? prev.order_by.filter((o) => o.column !== column)
        : [...prev.order_by, { column, direction: 'asc' }]
    }));
  };

  const toggleOrderDirection = (column) => {
    setMappingForm((prev) => ({
      ...prev,
      order_by: prev.order_by.map((o) =>
        o.column === column ? { ...o, direction: o.direction === 'asc' ? 'desc' : 'asc' } : o
      )
    }));
  };

  const handleSave = async () => {
    if (!mappingForm.name.trim()) return toast.error('Please enter a mapping name');
    if (!mappingForm.dataset_id) return toast.error('Please select a dataset');
    if (!mappingForm.xml_template_id) return toast.error('Please select an XML template');

    setSaving(true);
    try {
      if (selectedMapping) {
        await apiService.updateMapping(selectedMapping.id, mappingForm);
        toast.success('Mapping updated');
      } else {
        await apiService.createMapping(mappingForm);
        toast.success('Mapping created');
      }
      loadData();
      closeBuilder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (mapping) => {
    setDeletingMapping(mapping);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingMapping) return;
    try {
      await apiService.deleteMapping(deletingMapping.id);
      toast.success('Mapping deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete mapping');
    } finally {
      setDeleteDialogOpen(false);
      setDeletingMapping(null);
    }
  };

  // ---- Derived builder data ----

  // Tags shown to map = the template's {{placeholders}} PLUS any tag already in
  // the mapping (so existing/identity fields like an unplaceheld GUID still show).
  const tags = useMemo(() => {
    const all = extractTags(selectedTemplate?.xml_content);
    const mapped = (mappingForm.field_mappings || []).map((f) => f.xml_tag).filter(Boolean);
    const seen = new Set(), out = [];
    [...all, ...mapped].forEach((t) => { if (t && !seen.has(t)) { seen.add(t); out.push(t); } });
    return out;
  }, [selectedTemplate, mappingForm.field_mappings]);
  const columns = useMemo(() => selectedDataset?.columns || [], [selectedDataset]);
  const mappedCount = useMemo(() => tags.reduce((n, t) => n + (fieldByTag.has(t) ? 1 : 0), 0), [tags, fieldByTag]);
  const progress = tags.length ? Math.round((mappedCount / tags.length) * 100) : 0;

  const visibleTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tags.filter((t) => {
      if (q && !t.toLowerCase().includes(q)) return false;
      const mapped = fieldByTag.has(t);
      if (filterMode === 'mapped') return mapped;
      if (filterMode === 'unmapped') return !mapped;
      return true;
    });
  }, [tags, search, filterMode, fieldByTag]);

  const previewVouchers = useMemo(() => {
    if (!selectedTemplate || !sampleRows.length) return [];
    const sorted = sortRows(sampleRows, mappingForm.order_by);
    return groupRows(sorted, mappingForm.group_by);
  }, [selectedTemplate, sampleRows, mappingForm.order_by, mappingForm.group_by]);

  const previewXml = useMemo(() => {
    if (!selectedTemplate || !previewVouchers.length) return '';
    return previewVouchers
      .slice(0, 3)
      .map((g, i) => buildVoucherXml(selectedTemplate.xml_content || '', mappingForm.field_mappings, g, sampleGuid(i)))
      .join('\n\n<!-- ───────── next voucher ───────── -->\n\n');
  }, [selectedTemplate, previewVouchers, mappingForm.field_mappings]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // ==================== BUILDER VIEW ====================
  if (isBuilderOpen) {
    return (
      <div className="h-[calc(100vh-4rem)] flex flex-col overflow-hidden bg-slate-50" data-testid="mapping-builder">
        {/* Fixed toolbar (header + source/target picker) */}
        <div className="shrink-0">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={closeBuilder}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex-1 min-w-0">
            <Input
              placeholder="Untitled mapping"
              value={mappingForm.name}
              onChange={(e) => setMappingForm({ ...mappingForm, name: e.target.value })}
              className="h-9 max-w-sm font-heading font-semibold text-base border-transparent hover:border-slate-200 focus:border-teal-500 px-2"
              data-testid="mapping-name-input"
            />
          </div>

          {/* Progress */}
          <div className="hidden md:flex items-center gap-3 min-w-[200px]">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Mapped</span>
                <span className="text-xs font-medium text-slate-700">
                  {mappedCount}/{tags.length}
                </span>
              </div>
              <Progress value={progress} className="h-1.5" />
            </div>
          </div>

          <div className="h-6 w-px bg-slate-200" />
          <Button
            variant="outline"
            size="sm"
            onClick={autoMap}
            disabled={!selectedTemplate || !selectedDataset}
            data-testid="auto-map-btn"
          >
            <Sparkles className="h-4 w-4 mr-2 text-teal-600" />
            Auto-map
          </Button>
          <Button className="bg-teal-700 hover:bg-teal-600" onClick={handleSave} disabled={saving} data-testid="save-mapping-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
        </div>

        {/* Source/Target picker strip */}
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-slate-400" />
            <Label className="text-xs text-slate-500">Dataset</Label>
            <Select value={mappingForm.dataset_id} onValueChange={handleDatasetChange}>
              <SelectTrigger className="h-9 w-56" data-testid="mapping-dataset-select">
                <SelectValue placeholder="Select dataset" />
              </SelectTrigger>
              <SelectContent>
                {datasets.map((ds) => (
                  <SelectItem key={ds.id} value={ds.id}>
                    {ds.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedDataset && (
              <Badge className="badge-neutral">{columns.length} cols</Badge>
            )}
          </div>

          <ArrowUpDown className="h-4 w-4 text-slate-300 rotate-90" />

          <div className="flex items-center gap-2">
            <FileCode className="h-4 w-4 text-slate-400" />
            <Label className="text-xs text-slate-500">Template</Label>
            <Select value={mappingForm.xml_template_id} onValueChange={handleTemplateChange}>
              <SelectTrigger className="h-9 w-56" data-testid="mapping-template-select">
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((tmpl) => (
                  <SelectItem key={tmpl.id} value={tmpl.id}>
                    {tmpl.name} ({tmpl.voucher_type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplate && <Badge className="badge-neutral">{tags.length} tags</Badge>}
          </div>

          {previewVouchers.length > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <Badge className="badge-success">
                {previewVouchers.length} voucher{previewVouchers.length > 1 ? 's' : ''} from {sampleRows.length} rows
              </Badge>
            </div>
          )}
        </div>
        </div>
        {/* end toolbar */}

        {/* Main body */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* Left: field mappings */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 border-r border-slate-200 bg-white">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 shrink-0">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search XML tags..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 pl-8"
                  data-testid="tag-search"
                />
              </div>
              <div className="flex items-center rounded-md border border-slate-200 overflow-hidden text-xs">
                {['all', 'unmapped', 'mapped'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setFilterMode(m)}
                    className={`px-3 py-1.5 capitalize transition-colors ${
                      filterMode === m ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`filter-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
              <div className="p-4 space-y-2">
                {!selectedTemplate ? (
                  <EmptyHint icon={FileCode} text="Select an XML template to start mapping its tags." />
                ) : tags.length === 0 ? (
                  <EmptyHint icon={FileCode} text="No tags found in this template." />
                ) : visibleTags.length === 0 ? (
                  <EmptyHint icon={Search} text="No tags match your search/filter." />
                ) : (
                  visibleTags.map((tag) => (
                    <TagRow
                      key={tag}
                      tag={tag}
                      field={getField(tag)}
                      columns={columns}
                      hasDataset={!!selectedDataset}
                      onColumn={setFieldColumn}
                      onStatic={setFieldStatic}
                      onType={setFieldSourceType}
                      onScope={setFieldScope}
                      onPin={setFieldPin}
                      onClear={clearField}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right: Grouping + Preview */}
          <div className="w-[42%] min-w-[380px] flex flex-col min-h-0 bg-slate-50">
            <Tabs value={sideTab} onValueChange={setSideTab} className="flex-1 flex flex-col min-h-0">
              <div className="px-4 pt-3 bg-white border-b border-slate-200 shrink-0">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="preview" data-testid="tab-preview">
                    <Eye className="h-4 w-4 mr-2" />
                    Live Preview
                  </TabsTrigger>
                  <TabsTrigger value="grouping" data-testid="tab-grouping">
                    <Layers className="h-4 w-4 mr-2" />
                    Voucher Grouping
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Preview tab */}
              <TabsContent value="preview" className="flex-1 min-h-0 overflow-hidden m-0">
                <div className="h-full flex flex-col">
                  <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center justify-between shrink-0">
                    <span className="text-xs text-slate-500">
                      {sampleLoading
                        ? 'Loading sample data…'
                        : previewVouchers.length
                        ? `Showing ${Math.min(3, previewVouchers.length)} of ${previewVouchers.length} voucher(s)`
                        : 'Generated XML'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7"
                      onClick={() => loadSampleRows(mappingForm.dataset_id)}
                      disabled={!mappingForm.dataset_id || sampleLoading}
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${sampleLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {!selectedTemplate ? (
                      <EmptyHint icon={Eye} text="Pick a dataset and template to preview the XML." />
                    ) : !sampleRows.length ? (
                      <EmptyHint icon={Database} text="No sample rows. Preview the dataset first to load data." />
                    ) : (
                      <pre className="code-editor text-xs m-4 whitespace-pre-wrap break-words">{previewXml}</pre>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Grouping tab */}
              <TabsContent value="grouping" className="flex-1 min-h-0 overflow-hidden m-0">
                <div className="h-full overflow-y-auto custom-scrollbar">
                  <div className="p-4 space-y-6">
                    {!selectedDataset ? (
                      <EmptyHint icon={Layers} text="Select a dataset to configure grouping." />
                    ) : (
                      <>
                        {/* Group By */}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Layers className="h-4 w-4 text-teal-600" />
                            <Label className="text-sm font-medium">Group into single voucher by</Label>
                            <Badge className="badge-neutral">{mappingForm.group_by.length}</Badge>
                          </div>
                          <p className="text-xs text-slate-500 mb-3">
                            Rows sharing these column values merge into one voucher. Put the repeating
                            block between <code className="bg-slate-200 px-1 rounded">{'{{#LINEITEMS}}'}</code> …{' '}
                            <code className="bg-slate-200 px-1 rounded">{'{{/LINEITEMS}}'}</code> and set
                            line fields to <strong>Line</strong> scope.
                          </p>
                          <div className="flex flex-wrap gap-2" data-testid="group-by-columns">
                            {columns.map((col) => {
                              const active = mappingForm.group_by.includes(col);
                              return (
                                <button
                                  key={col}
                                  type="button"
                                  onClick={() => toggleGroupBy(col)}
                                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                                    active
                                      ? 'bg-teal-600 border-teal-600 text-white'
                                      : 'bg-white border-slate-300 text-slate-600 hover:border-teal-400'
                                  }`}
                                  data-testid={`group-by-${col}`}
                                >
                                  {col}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="h-px bg-slate-200" />

                        {/* Order By */}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <ArrowUpDown className="h-4 w-4 text-teal-600" />
                            <Label className="text-sm font-medium">Order by</Label>
                            <Badge className="badge-neutral">{mappingForm.order_by.length}</Badge>
                          </div>
                          <p className="text-xs text-slate-500 mb-3">
                            Sorts rows before posting — controls the order vouchers are sent and the
                            sequence of line items. Selection order sets sort priority.
                          </p>

                          {mappingForm.order_by.length > 0 && (
                            <div className="space-y-1.5 mb-3">
                              {mappingForm.order_by.map((spec, i) => (
                                <div
                                  key={spec.column}
                                  className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1.5"
                                >
                                  <span className="text-xs text-slate-400 w-4">{i + 1}</span>
                                  <span className="text-sm flex-1 truncate">{spec.column}</span>
                                  <button
                                    type="button"
                                    onClick={() => toggleOrderDirection(spec.column)}
                                    className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200"
                                    data-testid={`order-dir-${spec.column}`}
                                  >
                                    {spec.direction === 'desc' ? (
                                      <><ArrowDown className="h-3 w-3" /> desc</>
                                    ) : (
                                      <><ArrowUp className="h-3 w-3" /> asc</>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleOrderBy(spec.column)}
                                    className="text-slate-300 hover:text-red-500"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2" data-testid="order-by-columns">
                            {columns
                              .filter((c) => !mappingForm.order_by.some((o) => o.column === c))
                              .map((col) => (
                                <button
                                  key={col}
                                  type="button"
                                  onClick={() => toggleOrderBy(col)}
                                  className="px-2.5 py-1 rounded-full text-xs border border-dashed border-slate-300 text-slate-500 hover:border-teal-400 hover:text-teal-600 transition-colors"
                                  data-testid={`order-by-${col}`}
                                >
                                  + {col}
                                </button>
                              ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    );
  }

  // ==================== LIST VIEW ====================
  return (
    <div className="page-container" data-testid="mappings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Mappings</h1>
          <p className="text-sm text-slate-500 mt-1">Link dataset columns to Tally XML template tags</p>
        </div>
        <Button
          className="bg-teal-700 hover:bg-teal-600"
          onClick={() => openBuilder()}
          disabled={datasets.length === 0 || templates.length === 0}
          data-testid="create-mapping-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Mapping
        </Button>
      </div>

      {(datasets.length === 0 || templates.length === 0) && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <GitMerge className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Prerequisites Missing</p>
                <p className="text-sm text-amber-700">
                  You need at least one dataset and one XML template to create mappings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {mappings.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">
            <GitMerge className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No mappings created yet</p>
            <p className="text-sm">Click "Create Mapping" to link datasets with XML templates</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {mappings.map((mapping) => {
            const dataset = datasets.find((d) => d.id === mapping.dataset_id);
            const template = templates.find((t) => t.id === mapping.xml_template_id);
            const grouped = (mapping.group_by || []).length > 0;
            return (
              <Card
                key={mapping.id}
                className="group hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openBuilder(mapping)}
                data-testid={`mapping-row-${mapping.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-9 w-9 rounded-md bg-teal-50 flex items-center justify-center shrink-0">
                        <GitMerge className="h-4 w-4 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{mapping.name}</p>
                        <p className="text-xs text-slate-400">v{mapping.version}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          openBuilder(mapping);
                        }}
                        data-testid={`edit-mapping-${mapping.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(mapping);
                        }}
                        data-testid={`delete-mapping-${mapping.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                    <Badge className="badge-neutral truncate max-w-[45%]">{dataset?.name || 'Unknown'}</Badge>
                    <ArrowUpDown className="h-3 w-3 rotate-90 text-slate-300" />
                    <Badge className="badge-neutral truncate max-w-[45%]">{template?.name || 'Unknown'}</Badge>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                      <LinkIcon className="h-3 w-3 text-teal-600" />
                      {mapping.field_mappings?.length || 0} fields
                    </span>
                    {grouped && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                        <Layers className="h-3 w-3 text-teal-600" />
                        grouped by {mapping.group_by.length}
                      </span>
                    )}
                    {(mapping.order_by || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                        <ArrowUpDown className="h-3 w-3 text-teal-600" />
                        sorted
                      </span>
                    )}
                    <span className="ml-auto text-xs text-slate-400">
                      {format(parseISO(mapping.updated_at), 'MMM d, HH:mm')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Mapping</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingMapping?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-mapping-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Memoized so editing one field doesn't re-render all (potentially hundreds of)
// tag rows. Uses a native <select> for the column picker — far cheaper to mount
// at scale than a Radix Select per row.
const TagRow = React.memo(function TagRow({ tag, field, columns, hasDataset, onColumn, onStatic, onType, onScope, onPin, onClear }) {
  const isStatic = field?.type === 'static';
  const isGuid = field?.type === 'guid';
  const isPinned = !!field?.pin;
  const scope = field?.scope || 'header';
  return (
    <div
      className={`rounded-lg border p-3 transition-colors ${
        field ? 'border-teal-200 bg-teal-50/40' : 'border-slate-200 bg-white'
      }`}
      data-testid={`tag-row-${tag}`}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 w-1/3 min-w-0">
          {field ? (
            <CheckCircle2 className="h-4 w-4 text-teal-600 shrink-0" />
          ) : (
            <Circle className="h-4 w-4 text-slate-300 shrink-0" />
          )}
          <code className="text-sm font-mono text-slate-800 truncate" title={tag}>
            {tag}
          </code>
        </div>

        {/* Source type toggle */}
        <div className="flex items-center rounded-md border border-slate-200 overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => onType(tag, 'column')}
            className={`px-2 py-1 text-xs flex items-center gap-1 ${
              !isStatic && !isGuid ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            title="Map from a dataset column"
          >
            <Database className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onType(tag, 'static')}
            className={`px-2 py-1 text-xs flex items-center gap-1 border-l border-slate-200 ${
              isStatic ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            title="Use a fixed static value"
          >
            <Type className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onType(tag, 'guid')}
            className={`px-2 py-1 text-xs flex items-center gap-1 border-l border-slate-200 ${
              isGuid ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            title="Auto-generated unique GUID, saved per post for alter/repost"
          >
            <Fingerprint className="h-3 w-3" />
          </button>
        </div>

        {/* Value control */}
        <div className="flex-1 min-w-0">
          {isGuid ? (
            <div className="h-8 flex items-center gap-2 px-2.5 rounded-md border border-violet-200 bg-violet-50 text-xs text-violet-700">
              <Fingerprint className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-mono">Auto-generated per post</span>
            </div>
          ) : isStatic ? (
            <Input
              placeholder="Static value"
              value={field?.static_value || ''}
              onChange={(e) => onStatic(tag, e.target.value)}
              className="h-8"
              data-testid={`static-${tag}`}
            />
          ) : (
            (() => {
              // Options are ONLY the dataset's columns (table-qualified). Keep an
              // existing selection visible by matching on the column suffix, but
              // never invent options that aren't in the dataset.
              const dc = field?.dataset_column || '';
              const selected = !dc || columns.includes(dc)
                ? dc
                : (columns.find((c) => c === dc || c.split('.').pop() === dc || c.endsWith('.' + dc)) || '');
              return (
                <select
                  value={selected}
                  onChange={(e) => onColumn(tag, e.target.value)}
                  disabled={!hasDataset}
                  className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500/20 disabled:opacity-50"
                  data-testid={`column-${tag}`}
                >
                  <option value="" disabled>
                    Choose column
                  </option>
                  {columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              );
            })()
          )}
        </div>

        {/* Scope toggle */}
        <div
          className="flex items-center rounded-md border border-slate-200 overflow-hidden shrink-0"
          title="Header = once per voucher · Line = repeats per row inside {{#LINEITEMS}}"
        >
          <button
            type="button"
            onClick={() => onScope(tag, 'header')}
            className={`px-2 py-1 text-xs ${
              scope === 'header' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid={`scope-header-${tag}`}
          >
            Header
          </button>
          <button
            type="button"
            onClick={() => onScope(tag, 'line')}
            className={`px-2 py-1 text-xs border-l border-slate-200 ${
              scope === 'line' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
            data-testid={`scope-line-${tag}`}
          >
            Line
          </button>
        </div>

        {/* Pin/freeze toggle: save this value on the first post and reuse it on
            every repost, so it never changes in Tally (e.g. a renamed ledger
            keeps its original name and is updated in place, not recreated). */}
        <button
          type="button"
          onClick={() => onPin(tag, !isPinned)}
          disabled={!field || isGuid}
          className={`flex items-center gap-1 px-2 py-1 rounded-md border text-xs shrink-0 disabled:opacity-30 ${
            isPinned
              ? 'border-amber-300 bg-amber-100 text-amber-700'
              : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
          }`}
          title="Freeze value: saved on first post, reused on every repost (keeps a renamed record's original identity so Tally updates it in place instead of creating a new one)"
          data-testid={`pin-${tag}`}
        >
          <Lock className="h-3 w-3" />
        </button>

        <button
          type="button"
          onClick={() => onClear(tag)}
          disabled={!field}
          className="text-slate-300 hover:text-red-500 disabled:opacity-30 shrink-0"
          title="Clear mapping"
          data-testid={`clear-${tag}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

const EmptyHint = ({ icon: Icon, text }) => (
  <div className="text-center py-12 text-slate-400">
    <Icon className="h-10 w-10 mx-auto mb-3 opacity-40" />
    <p className="text-sm">{text}</p>
  </div>
);

export default MappingsPage;
